import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useColorModeValue,
  Divider,
} from "@chakra-ui/react";

import React, { useEffect, useMemo, useState } from "react";

import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import { useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import useAxios from "axios-hooks";
import {
  SearchIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  WarningIcon,
  ViewIcon,
} from "@chakra-ui/icons";
import {
  FaChargingStation,
  FaSort,
  FaSortUp,
  FaSortDown,
} from "react-icons/fa";
import { motion } from "framer-motion";

import LoadingSpinner from "components/feedback/LoadingSpinner";
import logger from "infra/logger";
import { loggedInUserState } from "atoms/loggedInUserState";

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionTr = motion(Tr);

const GlassMorphismCard = ({ children, ...rest }) => {
  const bgColor = useColorModeValue(
    "rgba(255, 255, 255, 0.8)",
    "rgba(26, 32, 44, 0.8)"
  );
  const borderColor = useColorModeValue(
    "rgba(255, 255, 255, 0.5)",
    "rgba(255, 255, 255, 0.1)"
  );

  return (
    <Box
      bg={bgColor}
      backdropFilter="blur(10px)"
      borderRadius="2xl"
      boxShadow="xl"
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      transition="all 0.3s ease"
      _hover={{ boxShadow: "2xl" }}
      {...rest}
    >
      {children}
    </Box>
  );
};

const EmptyStateIllustration = () => (
  <MotionFlex
    direction="column"
    align="center"
    justify="center"
    py={12}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
  >
    <Icon as={FaChargingStation} boxSize={16} color="#f57d36" mb={4} />
    <Text fontSize="xl" fontWeight="bold" mb={2}>
      No Locations Found
    </Text>
    <Text color="gray.500" textAlign="center" maxW="md">
      There are no locations available at the moment. New locations will appear
      here once added.
    </Text>
  </MotionFlex>
);

const StatusBadge = ({ status }) => {
  let color, icon, label;

  switch (status.toLowerCase()) {
    case "active":
      color = "green";
      icon = CheckCircleIcon;
      label = "Active";
      break;
    case "inactive":
      color = "red";
      icon = WarningIcon;
      label = "Inactive";
      break;
    default:
      color = "green";
      icon = CheckCircleIcon;
      label = "Active";
  }

  return (
    <Badge
      display="flex"
      alignItems="center"
      px={3}
      py={1}
      borderRadius="full"
      colorScheme={color}
      variant="subtle"
    >
      <Icon as={icon} mr={1} boxSize={3} />
      <Text fontSize="xs" fontWeight="medium">
        {label}
      </Text>
    </Badge>
  );
};

const LocationAvatar = ({ name }) => {
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .substring(0, 2);

  return (
    <Avatar
      name={name}
      size="sm"
      bg="#f57d36"
      color="white"
      fontWeight="bold"
      getInitials={() => initials}
    />
  );
};

const PaginationButton = ({ children, isActive, isDisabled, onClick }) => {
  return (
    <Button
      size="sm"
      variant={isActive ? "solid" : "outline"}
      colorScheme={isActive ? "orange" : "gray"}
      isDisabled={isDisabled}
      onClick={onClick}
      minW="40px"
      h="40px"
      borderRadius="md"
      _hover={!isDisabled && { transform: "translateY(-2px)", shadow: "md" }}
      transition="all 0.2s"
    >
      {children}
    </Button>
  );
};

const tableColumns = [
  {
    Header: "S.No",
  },
  {
    Header: "Name",
  },
  {
    Header: "PinCode",
  },
  {
    Header: "Address",
  },
  {
    Header: "Actions",
  },
];

export default function AvailableLocationsTable() {
  const [locations, setLocations] = useState([]);
  const loggerInUser = useRecoilValue(loggedInUserState);
  const navigate = useNavigate();
  const [{ data: locationData, loading }, getLocations] = useAxios(
    { method: "GET", url: `/api/locations` },
    { manual: true }
  );

  const [searchQuery, setSearchQuery] = useState("");
  const columns = useMemo(() => tableColumns, []);
  const data = useMemo(() => locations, [locations]);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        pageSize: 10,
      },
      globalFilter: (rows, ids, query) => {
        if (!query || query.trim() === "") {
          return rows;
        }

        const lowercasedQuery = query.toLowerCase().trim();

        return rows.filter((row) => {
          const name = row.original.Name || "";
          const uid = row.original.Uid || "";
          const city = row.original.City || "";
          const state = row.original.State || "";
          const postalCode = row.original.PostalCode || "";
          const address = row.original.Address || "";

          return (
            name.toLowerCase().includes(lowercasedQuery) ||
            uid.toLowerCase().includes(lowercasedQuery) ||
            city.toLowerCase().includes(lowercasedQuery) ||
            state.toLowerCase().includes(lowercasedQuery) ||
            postalCode.toLowerCase().includes(lowercasedQuery) ||
            address.toLowerCase().includes(lowercasedQuery)
          );
        });
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    state: { pageIndex, pageSize },
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
  } = tableInstance;

  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const hoverBg = useColorModeValue("rgba(245, 125, 54, 0.08)", "rgba(245, 125, 54, 0.15)");
  const themeColor = "#f57d36";
  const theadBgColor = useColorModeValue("gray.50", "gray.800");
  const cardBg = useColorModeValue("white", "gray.800");

  const fetchLocations = async () => {
    try {
      const response = await getLocations();
      logger.log(response.data.data);
      setLocations(response.data.data);
    } catch (error) {
      logger.log(error);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setGlobalFilter(value);
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getTimeSince = (dateString) => {
    if (!dateString) return "Never";

    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    let interval = Math.floor(seconds / 31536000);
    if (interval > 1) return interval + " years ago";

    interval = Math.floor(seconds / 2592000);
    if (interval > 1) return interval + " months ago";

    interval = Math.floor(seconds / 86400);
    if (interval > 1) return interval + " days ago";

    interval = Math.floor(seconds / 3600);
    if (interval > 1) return interval + " hours ago";

    interval = Math.floor(seconds / 60);
    if (interval > 1) return interval + " minutes ago";

    return "Just now";
  };

  return loading ? (
    <LoadingSpinner />
  ) : locations.length > 0 ? (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      w="100%"
    >
      <GlassMorphismCard p={0}>
        <Box p={6} borderBottom="1px solid" borderColor={borderColor}>
          <Flex
            direction={{ base: "column", md: "row" }}
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            gap={4}
          >
            <Box>
              <Text fontSize="xl" fontWeight="bold" color={textColor}>
                Total {locations.length} Locations
              </Text>
              <Text fontSize="sm" color="gray.500" mt={1}>
                Manage and monitor all charging locations
              </Text>
            </Box>

            <InputGroup maxW={{ base: "100%", md: "300px" }}>
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search locations..."
                value={searchQuery}
                onChange={handleSearchChange}
                borderRadius="lg"
                _focus={{
                  borderColor: themeColor,
                  boxShadow: `0 0 0 1px ${themeColor}`,
                }}
              />
            </InputGroup>
          </Flex>
        </Box>

        {/* Mobile Card View */}
        <VStack display={{ base: "flex", lg: "none" }} spacing={3} p={4} align="stretch">
          {page.map((row, index) => {
            prepareRow(row);
            const location = row.original;
            return (
              <Box
                key={location?.Id || index}
                bg={cardBg}
                border="1px solid"
                borderColor={borderColor}
                borderRadius="md"
                p={3}
                cursor="pointer"
                onClick={() => navigate(`/admin/charging_management/view/${location?.Id}/chargers`)}
                _hover={{ borderColor: themeColor, boxShadow: "md" }}
                transition="all 0.2s"
              >
                <Flex justify="space-between" align="center" mb={2}>
                  <Flex align="center" gap={2}>
                    <LocationAvatar name={location?.Name} />
                    <Box>
                      <Text fontSize="sm" fontWeight="bold" color={textColor}>
                        {location?.Name}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {location?.City}, {location?.State}
                      </Text>
                    </Box>
                  </Flex>
                  <IconButton
                    icon={<ViewIcon />}
                    colorScheme="orange"
                    variant="ghost"
                    size="sm"
                    borderRadius="full"
                    aria-label="View"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/admin/charging_management/view/${location?.Id}/chargers`);
                    }}
                  />
                </Flex>
                <Divider mb={2} />
                <Flex justify="space-between" align="center" gap={3}>
                  <Box flex={1}>
                    <Text fontSize="2xs" color="gray.500" fontWeight="semibold">
                      PINCODE
                    </Text>
                    <Text fontSize="xs" fontWeight="medium" color={textColor}>
                      {location?.PostalCode || "N/A"}
                    </Text>
                  </Box>
                  <Box flex={2}>
                    <Text fontSize="2xs" color="gray.500" fontWeight="semibold">
                      ADDRESS
                    </Text>
                    <Text 
                      fontSize="xs" 
                      fontWeight="medium" 
                      color={textColor}
                      noOfLines={2}
                      overflow="hidden"
                      textOverflow="ellipsis"
                    >
                      {location?.Address || "N/A"}
                    </Text>
                  </Box>
                </Flex>
              </Box>
            );
          })}
        </VStack>

        {/* Desktop Table View */}
        <TableContainer display={{ base: "none", lg: "block" }}>
          <Table {...getTableProps()} variant="simple" colorScheme="gray">
            <Thead bg={theadBgColor}>
              {headerGroups.map((headerGroup, index) => (
                <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                  {headerGroup.headers.map((column, index) => (
                    <Th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      key={index}
                      py={4}
                      px={6}
                      borderColor={borderColor}
                      textTransform="uppercase"
                      fontSize="xs"
                      fontWeight="bold"
                      letterSpacing="wider"
                      color="gray.500"
                      textAlign="center"
                      cursor="pointer"
                      _hover={{ color: themeColor }}
                      transition="all 0.2s"
                    >
                      <Flex justify="center" align="center" gap={1}>
                        {column.render("Header")}
                        <Box>
                          {column.isSorted ? (
                            column.isSortedDesc ? (
                              <Icon as={FaSortDown} />
                            ) : (
                              <Icon as={FaSortUp} />
                            )
                          ) : (
                            <Icon as={FaSort} opacity={0.3} />
                          )}
                        </Box>
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody {...getTableBodyProps()}>
              {page.map((row, index) => {
                prepareRow(row);
                return (
                  <MotionTr
                    {...row.getRowProps()}
                    key={index}
                    whileHover={{ scale: 1.001 }}
                    transition={{ duration: 0.15 }}
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate(`/admin/charging_management/view/${row.original.Id}/chargers`)}
                    sx={{
                      _hover: {
                        bg: hoverBg,
                        transform: "translateX(2px)",
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    {row.cells.map((cell, index) => {
                      let data = "";
                      if (cell.column.Header === "S.No") {
                        data = (
                          <Flex align="center" justify="center">
                            <Text
                              color={textColor}
                              fontSize="sm"
                              fontWeight="600"
                            >
                              {Number(cell.row.id) + 1}
                            </Text>
                          </Flex>
                        );
                      } else if (cell.column.Header === "Name") {
                        data = (
                          <Flex align="center" gap={3}>
                            <LocationAvatar name={cell.row.original.Name} />
                            <Box>
                              <Text color={textColor} fontWeight="semibold">
                                {cell.row.original.Name}
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                {cell.row.original.City},{" "}
                                {cell.row.original.State}
                              </Text>
                            </Box>
                          </Flex>
                        );
                      } else if (cell.column.Header === "PinCode") {
                        data = (
                          <Flex align="center" justify="center">
                            <Text color={textColor} fontSize="sm">
                              {cell?.row?.original?.PostalCode || "N/A"}
                            </Text>
                          </Flex>
                        );
                      } else if (cell.column.Header === "Address") {
                        const address = cell?.row?.original?.Address || "N/A";
                        const truncatedAddress = address.length > 60 ? address.substring(0, 60) + "..." : address;
                        data = (
                          <Flex align="center" justify="center">
                            <Tooltip label={address} placement="top" hasArrow>
                              <Text color={textColor} fontSize="sm" textAlign="center">
                                {truncatedAddress}
                              </Text>
                            </Tooltip>
                          </Flex>
                        );
                      } else if (cell.column.Header === "Actions") {
                        data = (
                          <Flex align="center" justify="center" gap={2}>
                            <Tooltip
                              label="View Details"
                              placement="top"
                              hasArrow
                            >
                              <IconButton
                                icon={<ViewIcon />}
                                colorScheme="blue"
                                variant="ghost"
                                size="sm"
                                borderRadius="full"
                                aria-label="View"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(
                                    `/admin/charging_management/view/${cell.row.original.Id}/chargers`
                                  );
                                }}
                              />
                            </Tooltip>
                          </Flex>
                        );
                      }
                      return (
                        <Td
                          {...cell.getCellProps()}
                          key={index}
                          py={4}
                          px={6}
                          borderColor={borderColor}
                          fontSize="sm"
                        >
                          {data}
                        </Td>
                      );
                    })}
                  </MotionTr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Flex
          p={4}
          justify="space-between"
          align="center"
          borderTop="1px solid"
          borderColor={borderColor}
          flexWrap="wrap"
          gap={4}
        >
          <HStack>
            <Select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              size="sm"
              borderRadius="md"
              w="120px"
            >
              {[5, 10, 20, 30, 40, 50].map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </Select>
            <Text fontSize="sm" color="gray.500">
              Showing {page.length} of {locations.length} results
            </Text>
          </HStack>

          <HStack spacing={2}>
            <PaginationButton
              isDisabled={!canPreviousPage}
              onClick={() => gotoPage(0)}
            >
              <ChevronLeftIcon boxSize={4} mr={1} />
              First
            </PaginationButton>

            <PaginationButton
              isDisabled={!canPreviousPage}
              onClick={() => previousPage()}
            >
              <ChevronLeftIcon boxSize={4} />
            </PaginationButton>

            {pageCount <= 5 ? (
              [...Array(pageCount)].map((_, i) => (
                <PaginationButton
                  key={i}
                  isActive={pageIndex === i}
                  onClick={() => gotoPage(i)}
                >
                  {i + 1}
                </PaginationButton>
              ))
            ) : (
              <>
                {pageIndex > 1 && (
                  <PaginationButton onClick={() => gotoPage(0)}>
                    1
                  </PaginationButton>
                )}

                {pageIndex > 2 && <Text>...</Text>}

                {pageIndex > 0 && (
                  <PaginationButton onClick={() => gotoPage(pageIndex - 1)}>
                    {pageIndex}
                  </PaginationButton>
                )}

                <PaginationButton isActive onClick={() => gotoPage(pageIndex)}>
                  {pageIndex + 1}
                </PaginationButton>

                {pageIndex < pageCount - 1 && (
                  <PaginationButton onClick={() => gotoPage(pageIndex + 1)}>
                    {pageIndex + 2}
                  </PaginationButton>
                )}

                {pageIndex < pageCount - 3 && <Text>...</Text>}

                {pageIndex < pageCount - 2 && (
                  <PaginationButton onClick={() => gotoPage(pageCount - 1)}>
                    {pageCount}
                  </PaginationButton>
                )}
              </>
            )}

            <PaginationButton
              isDisabled={!canNextPage}
              onClick={() => nextPage()}
            >
              <ChevronRightIcon boxSize={4} />
            </PaginationButton>

            <PaginationButton
              isDisabled={!canNextPage}
              onClick={() => gotoPage(pageCount - 1)}
            >
              Last
              <ChevronRightIcon boxSize={4} ml={1} />
            </PaginationButton>
          </HStack>
        </Flex>
      </GlassMorphismCard>
    </MotionBox>
  ) : (
    <GlassMorphismCard w="100%" py={8}>
      <EmptyStateIllustration />
    </GlassMorphismCard>
  );
}
